<template>
    <div class="commissions-forecast-reorder">
        <transition name="dialogfade">
            <k-dialog v-if="showDialog" class="default__dialog commissions-forecast-reorder-dialog"
                      @close="onCancelClicked"
                      :title="'Edit Player Ordering'">
                <div class="dialog-body">
                    <div class="filters-container">
                        <div class="reorder-info">
                            <p class="info-text"><i class="fa-regular fa-circle-info"></i> Sort players by column or drag and drop players to reorder them</p>
                            <p class="info-note">Note: <span>Clicking Save will automatically group active contract players before non-contract players</span></p>
                        </div>
                        <div class="player-type-dropdown-container">
                            <label>
                                Player Group
                                <k-dropdown :data-items="playerTypeSelects"
                                            class="player-type-dropdown"
                                            v-model="selectedPlayerType"
                                            :text-field="'display'"
                                            @change="assignSelectedPlayerRows"></k-dropdown>
                            </label>
                        </div>
                    </div>
                    <div class="grid-container">
                        <k-grid :data-items="selectedPlayerRows"
                                :columns="playerColumns"
                                :cell-render="'dragTemplate'"
                                :class="isDragging"
                                :style="{height: 100 + '%'}"
                                :sortable="sortable"
                                :sort="selectedPlayerType.value === activeContractPlayersValue ? activeContractsSort : nonContractsSort"
                                @sortchange="onSortChange"
                                ref="orderingGrid">
                            <template v-slot:dragTemplate="{ props }">
                                <k-draggable v-if="props.field === 'reorder'"
                                            @press="onDragPress($event, props.dataItem)"
                                            @drag="onDrag($event, props.dataItem)"
                                            @release="onDragRelease($event, props.dataItem)"
                                            ref="draggable">
                                    <td :data-itemid="props.dataItem.dataId" :class="dragCellClass" :style="{textAlign: 'center'}">
                                        <span class="drag-cursor"><i class="fa-regular fa-bars"></i></span>
                                    </td>
                                </k-draggable>
                                <td v-if="props.field === 'csvName'" :data-itemid="props.dataItem.dataId" :class="dragCellClass">
                                    {{ getNestedValue(props.field, props.dataItem) }}
                                </td>
                                <td v-if="props.field.includes('Salary')" :data-itemid="props.dataItem.dataId" :class="dragCellClass">
                                    <span :class="[{'projected': props.dataItem[`${props.field.split('Salary')[0]}Projected`]}]">{{ $options.filters.formatCurrency(getNestedValue(props.field, props.dataItem)) }}</span>
                                </td>
                            </template>
                        </k-grid>
                        <div v-if="showDropHints"
                             class="k-grid drag-clue"     
                             :style="{
                                display: 'block',
                                position: 'absolute',
                                zIndex: 20000,
                                padding: '8px 12px',
                                height: 'unset',
                                top: dragTop + 'px',
                                left: '100px',
                            }">
                            <table class="k-grid-table">
                                <tr>
                                    <td class="k-master-row k-alt">
                                        <span :class="hintIconClass" style="cursor: 'move'"></span>
                                    </td>
                                    <td class="k-master-row k-alt">{{ activeRow.displayName }}</td>
                                </tr>
                            </table>
                        </div>
                    </div>
                </div>

                <div class="loader-container" v-if="isLoading">
                    <k-loader :size="'large'" :type="'infinite-spinner'"></k-loader>
                </div>

                <k-action-bar>
                    <k-button class="item-save__btn" @click="onSaveClicked">Save</k-button>
                    <k-button class="item-cancel__btn" @click="onCancelClicked">Cancel</k-button>
                </k-action-bar>
            </k-dialog>
        </transition>

        <transition name="dialogfade">
            <k-dialog v-if="showErrorDialog" :title="errorTitle" class="error__dialog" @close="toggleErrorDialog">
                <p class="error-dialog__msg">{{ errorMsg }}</p>
            </k-dialog>
        </transition>
    </div>
</template>

<script>
    import { Dialog, DialogActionsBar } from '@progress/kendo-vue-dialogs';
    import { Button } from "@progress/kendo-vue-buttons";
    import { Grid } from '@progress/kendo-vue-grid';
    import { Draggable } from '@progress/kendo-vue-common';
    import { Loader } from "@progress/kendo-vue-indicators";
    import { DropDownList } from '@progress/kendo-vue-dropdowns';

    import { orderBy } from '@progress/kendo-data-query';

    import cloneDeep from 'lodash.clonedeep';
    import isEqual from 'lodash.isequalwith';

    import auth from '../authConfig';
    import apiService from '../api/api.service.js';

    export default {
        name: 'commissionsForecastReorderDialog',
        props: ['seasonStrings', 'showDialog'],
        emits: ['dialogclosed', 'playerorderupdated'],
        data () {
            return {
                activeContractPlayerRows: [],
                activeContractPlayersValue: 'activeContractPlayers',
                activeContractsSort: [],
                activeRow: {},
                allContractsPlayersValue: 'allPlayers',
                allPlayerRows: [],
                draggable: null,
                dragLeft: 0,
                dragTop: 0,
                dropPosition: '',
                errorMsg: '',
                errorTitle: '',
                forecastData: null,
                isDragged: false,
                isLoading: false,
                nonContractPlayerRows: [],
                nonContractPlayersValue: 'nonContractPlayers',
                nonContractsSort: [],
                playerTypeSelects: [
                    { display: 'Active Contract Players', value: 'activeContractPlayers' },
                    { display: 'Non-Contract Players', value: 'nonContractPlayers' }
                ],
                selectedPlayerRows: [],
                selectedPlayerType: { display: 'Active Contract Players', value: 'activeContractPlayers' },
                showDragClueContent: false,
                showDropHints: false,
                showErrorDialog: false, 
                sortable: { allowUnsort: true, mode: 'multiple' },
                unsortedActiveContractPlayerRows: [],
                unsortedNonContractPlayerRows: []
            }
        },
        computed: {
            currentSeasonStartYear() {
                let currentMonth = new Date().getMonth();
                let currentYear = new Date().getFullYear();

                if (currentMonth >= 6) {
                    return currentYear;
                } else {
                    return currentYear - 1;
                }
            },
            dragCellClass() {
                return this.dropPosition === 'above' ? 'above' : 'below';
            },
            hintIconClass() {
                return this.showDragClueContent ? 'k-icon k-i-insert-middle' : 'k-icon k-i-cancel';
            },
            isDragging() {
                return { dragging: this.activeRow.dataId };
            },
            playerColumns() {
                if (this.seasonStrings) {
                    return [
                        { field: 'reorder', title: ' ', width: '60px' },
                        { field: 'csvName', title: 'Player' },
                        { field: 'year1Salary', title: `${this.seasonStrings[0]} Salary` },
                        { field: 'year2Salary', title: `${this.seasonStrings[1]} Salary` },
                        { field: 'year3Salary', title: `${this.seasonStrings[2]} Salary`}
                    ]
                }

                return [];
            }
        },
        watch: {
            showDialog(val) {
                if (val) {
                    this.getCommissionForecastDataForReordering();
                } else {
                    this.resetDefaults();
                }
            }
        },
        beforeUnmount() {
            let gridContent = this.$refs.orderingGrid?.$el.querySelector('.k-grid-content');

            if (gridContent) {
                gridContent.removeEventListener('mouseover', this.gridMouseover, false);
                gridContent.removeEventListener('mouseout', this.gridMouseout, false);
            }
        },
        methods: {
            addGridEventListeners() {
                let gridContent = this.$refs.orderingGrid?.$el.querySelector('.k-grid-content');

                if (gridContent) {
                    gridContent.addEventListener('mouseover', this.gridMouseover, false);
                    gridContent.addEventListener('mouseout', this.gridMouseout, false);
                }
            },
            assignSelectedPlayerRows() {
                if (this.selectedPlayerType.value === this.activeContractPlayersValue) {
                    this.selectedPlayerRows = this.activeContractPlayerRows;
                }

                if (this.selectedPlayerType.value === this.nonContractPlayersValue) {
                    this.selectedPlayerRows = this.nonContractPlayerRows;
                }

                if (this.selectedPlayerType.value === this.allContractsPlayersValue) {
                    this.selectedPlayerRows = [...this.activeContractPlayerRows, ...this.nonContractPlayerRows];
                }
            },
            async getCommissionForecastDataForReordering() {
                this.isLoading = true;

                let tokenResponse = await auth.acquireTokenResponse();

                return apiService.GetCommissionForecastDataForReordering(tokenResponse.accessToken, this.currentSeasonStartYear)
                    .then((res) => {
                        console.log(res);
                        this.isLoading = false;
                        
                        if (res.data?.data) {
                            this.forecastData = res.data.data;
                            this.activeContractPlayerRows = cloneDeep(this.forecastData.activeContractPlayers);
                            this.unsortedActiveContractPlayerRows = cloneDeep(this.forecastData.activeContractPlayers);
                            this.nonContractPlayerRows = cloneDeep(this.forecastData.nonContractPlayers);
                            this.unsortedNonContractPlayerRows = cloneDeep(this.forecastData.nonContractPlayers);
                            this.allPlayerRows = [...this.forecastData.activeContractPlayers, ...this.forecastData.nonContractPlayers];
                            this.assignSelectedPlayerRows();
                            this.addGridEventListeners();
                        }
                    }).catch(error => {
                        console.log(error);
                        self.onRequestError('An Error Occurred', error?.response?.data?.message);
                    })
            },
            getDroppedDataId(target) {
                if (target.getAttribute('data-itemid')) {
                    return target.getAttribute('data-itemid');
                } else {
                    if (target.parentElement) {
                        return this.getDroppedDataId(target.parentElement);
                    }

                    return false;
                }
            },
            getDropPosition(evt) {
                return evt.offsetY >= evt.originalEvent.target.parentElement.clientHeight / 2
                    ? 'below'
                    : 'above';
            },
            getNestedValue(fieldName, item) {
                let nameArray = fieldName.split('.');
                let data = item;
                nameArray.forEach(name => {
                    data = data ? data[name] : undefined;
                });

                return data;
            },
            getTargetTopOffset(evt) {
                let gridHeader = this.$refs.orderingGrid?.$el.querySelector('.k-grid-header');
                let grid = this.$refs.orderingGrid?.$el;

                if (evt.originalEvent.target.className !== 'k-drop-hint-line' && evt.originalEvent.target.className !== 'k-drop-hint k-drop-hint-h') {
                    return evt.offsetY >= evt.originalEvent.target.parentElement.clientHeight / 2
                        ? evt.originalEvent.target.parentElement.offsetTop + gridHeader.offsetHeight + grid.offsetTop + evt.originalEvent.target.parentElement.clientHeight
                        : evt.originalEvent.target.parentElement.offsetTop + gridHeader.offsetHeight + grid.offsetTop;
                }
            },
            gridMouseout() {
                this.showDragClueContent = false;
            },
            gridMouseover() {
                this.showDragClueContent = true;
            },
            onCancelClicked() {
                this.$emit('dialogclosed');
            },
            onDrag(evt, item) {
                this.dragTop = evt.pageY - 250;
                this.dragLeft = evt.pageX;
                this.dropPosition = this.getDropPosition(evt);
                this.showDropHints = true;
            },
            onDragPress(evt, item) {
                //let row = this.draggable && this.draggable.element;

                if (!evt.isTouch) {
                    evt.originalEvent.preventDefault();
                }

                this.isDragged = true;
                this.activeRow = item;

                // if (row) {
                //     this.activeRow = item;
                // }
            },
            onDragRelease(evt, item) {
                this.showDropHints = false;
                let reorderedData = this.reorderData(evt);

                this.selectedPlayerRows = reorderedData;

                if (this.selectedPlayerType.value == this.activeContractPlayersValue) {
                    this.activeContractPlayerRows = this.selectedPlayerRows;
                    this.unsortedActiveContractPlayerRows = this.selectedPlayerRows;
                }

                if (this.selectedPlayerType.value == this.nonContractPlayersValue) {
                    this.nonContractPlayerRows = this.selectedPlayerRows;
                    this.unsortedNonContractPlayerRows = this.selectedPlayerRows;
                }
                this.activeRow = {};
            },
            onRequestError(errorTitle, errorMsg) {
                this.isLoading = false;

                this.errorTitle = errorTitle;

                if (errorMsg) {
                    this.errorMsg = errorMsg;
                } else {
                    this.errorMsg = 'Please try again';
                }

                this.showErrorDialog = true;
            },
            onSaveClicked() {
                var updateRequest = [];
                this.activeContractPlayerRows.forEach((player, i) => {
                    player.sortOrder = i + 1;

                    let originalPlayer = this.forecastData.activeContractPlayers.find(p => p.playerId === player.playerId);

                    if (originalPlayer && originalPlayer.sortOrder != player.sortOrder) {
                        updateRequest.push({
                            playerId: player.playerId,
                            sortOrder: player.sortOrder,
                            contractNotes: player.contractNotes,
                            name: player.csvName
                        });
                    }
                });

                let startingSort = this.activeContractPlayerRows.length + 1;

                this.nonContractPlayerRows.forEach((player, i) => {
                    player.sortOrder = startingSort + i;

                    let originalPlayer = this.forecastData.nonContractPlayers.find(p => p.playerId === player.playerId);

                    if (originalPlayer && originalPlayer.sortOrder != player.sortOrder) {
                        updateRequest.push({
                            playerId: player.playerId,
                            sortOrder: player.sortOrder,
                            contractNotes: player.contractNotes,
                            name: player.csvName
                        });
                    }
                })

                console.log(updateRequest);

                if (updateRequest.length > 0) {
                    this.$emit('playerorderupdated', updateRequest);
                } else {
                    this.$emit('dialogclosed');
                }
            },
            onSortChange(evt) {
                if (this.selectedPlayerType.value == this.activeContractPlayersValue) {
                    this.activeContractsSort = evt.sort;
                }

                if (this.selectedPlayerType.value == this.nonContractPlayersValue) {
                    this.nonContractsSort = evt.sort;
                }

                if (evt.sort && evt.sort.length > 0) {
                    this.selectedPlayerRows = orderBy(this.selectedPlayerRows, evt.sort);

                    if (this.selectedPlayerType.value == this.activeContractPlayersValue) {
                        this.activeContractPlayerRows = this.selectedPlayerRows;
                    }
                    if (this.selectedPlayerType.value == this.nonContractPlayersValue) {
                        this.nonContractPlayerRows = this.selectedPlayerRows;
                    }
                } else {
                    if (this.selectedPlayerType.value == this.activeContractPlayersValue) {
                        this.selectedPlayerRows = this.unsortedActiveContractPlayerRows;
                        this.activeContractPlayerRows = this.unsortedActiveContractPlayerRows;
                    }
                    if (this.selectedPlayerType.value == this.nonContractPlayersValue) {
                        this.selectedPlayerRows = this.unsortedNonContractPlayerRows;
                        this.nonContractPlayerRows = this.unsortedNonContractPlayerRows;
                    }
                }
            },
            reorderData(evt) {
                let dropItemDataId = this.getDroppedDataId(evt.originalEvent.target);
                let reorderedData = this.selectedPlayerRows;

                if (dropItemDataId) {
                    let activeItemIndex = reorderedData.findIndex(i => i.dataId === this.activeRow.dataId);

                    let hoveredItemIndex = reorderedData.findIndex(i => i.dataId === dropItemDataId);

                    if (activeItemIndex !== hoveredItemIndex) {
                        reorderedData.splice(activeItemIndex, 1);

                        if (this.dropPosition === 'above') {
                            if (hoveredItemIndex === 0) {
                                reorderedData.unshift({...this.activeRow});
                            } else {
                                if (hoveredItemIndex < activeItemIndex) {
                                    reorderedData.splice(hoveredItemIndex, 0, {...this.activeRow});
                                }

                                if (hoveredItemIndex > activeItemIndex) {
                                    reorderedData.splice(hoveredItemIndex - 1, 0, {...this.activeRow});
                                }
                            }
                        }

                        if (this.dropPosition === 'below') {
                            if (hoveredItemIndex !== 0) {
                                if (hoveredItemIndex < activeItemIndex) {
                                    reorderedData.splice(hoveredItemIndex + 1, 0, {...this.activeRow});
                                }

                                if (hoveredItemIndex > activeItemIndex) {
                                    reorderedData.splice(hoveredItemIndex, 0, {...this.activeRow});
                                }
                            }
                        }
                    }

                    return reorderedData;
                }
            },
            resetDefaults() {
                this.activeRow = {};

                this.forecastData = null;
                this.selectedPlayerRows = [];
                this.activeContractPlayerRows = [];
                this.unsortedActiveContractPlayerRows = [];
                this.nonContractPlayerRows = [];
                this.unsortedNonContractPlayerRows = [];
                this.allPlayerRows = [];

                this.activeContractsSort = [];
                this.nonContractsSort = [];
                this.selectedPlayerType = { display: 'Active Contract Players', value: 'activeContractPlayers' };
            },
            toggleErrorDialog() {
                this.showErrorDialog = !this.showErrorDialog;

                if(!this.showErrorDialog) {
                    this.errorTitle = '';
                    this.errorMsg = '';
                }
            }
        },
        filters: {
            formatCurrency(val) {
                if (val === null || val === undefined || val === '') return '';

                if (val.toString().includes('$')) return val;

                if (isNaN(val)) {
                    val = 0;
                }

                let formatter = new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                });

                return formatter.format(val);
            }
        },
        components: {
            'k-dialog': Dialog,
            'k-action-bar': DialogActionsBar,
            'k-grid': Grid,
            'k-loader': Loader,
            'k-draggable': Draggable,
            'k-dropdown': DropDownList,
            'k-button': Button
        }
    }
</script>

<style>
    @import '/src/assets/css/commissionsForecastReorderDialog.css';
</style>